import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { adminAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { FiPlus, FiEdit2, FiTrash2 } from 'react-icons/fi';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import './Manage.css';

const ManageSkills = () => {
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', proficiency: 50, iconClass: '', category: 0, displayOrder: 0 });

  const loadSkills = async () => {
    try {
      const res = await adminAPI.getSkills();
      if (res.success) setSkills(res.data);
    } catch (err) { toast.error('Failed to load skills'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadSkills(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', description: '', proficiency: 50, iconClass: '', category: 0, displayOrder: 0 });
    setShowModal(true);
  };

  const openEdit = (skill) => {
    setEditing(skill);
    const catMap = ['Backend', 'Frontend', 'Database', 'Tools', 'Broadcast', 'Other'];
    setForm({
      name: skill.name,
      description: skill.description || '',
      proficiency: skill.proficiency,
      iconClass: skill.iconClass || '',
      category: catMap.indexOf(skill.category),
      displayOrder: skill.displayOrder,
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) { await adminAPI.updateSkill(editing.id, form); toast.success('Skill updated'); }
      else { await adminAPI.createSkill(form); toast.success('Skill created'); }
      setShowModal(false);
      loadSkills();
    } catch (err) { toast.error('Operation failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this skill?')) return;
    try { await adminAPI.deleteSkill(id); toast.success('Skill deleted'); loadSkills(); }
    catch (err) { toast.error('Failed to delete'); }
  };

  const categories = ['Backend', 'Frontend', 'Database', 'Tools', 'Broadcast', 'Other'];

  if (loading) return <LoadingSpinner />;

  return (
    <div className="manage-page">
      <Helmet><title>Manage Skills | Portfolio CMS</title></Helmet>
      <div className="manage-header">
        <h2>Skills Management</h2>
        <button className="btn btn-primary" onClick={openCreate}><FiPlus /> New Skill</button>
      </div>

      <div className="manage-table">
        <table>
          <thead><tr><th>Name</th><th>Category</th><th>Proficiency</th><th>Order</th><th>Actions</th></tr></thead>
          <tbody>
            {skills.map(s => (
              <tr key={s.id}>
                <td><strong>{s.name}</strong></td>
                <td><span className="status-pill">{s.category}</span></td>
                <td><div className="mini-progress"><div className="mini-progress-fill" style={{width: `${s.proficiency}%`}}></div></div> {s.proficiency}%</td>
                <td>{s.displayOrder}</td>
                <td><div className="action-btns">
                  <button className="btn-icon edit" onClick={() => openEdit(s)}><FiEdit2 /></button>
                  <button className="btn-icon delete" onClick={() => handleDelete(s.id)}><FiTrash2 /></button>
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>{editing ? 'Edit Skill' : 'New Skill'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group"><label>Name</label><input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required /></div>
              <div className="form-group"><label>Description</label><input value={form.description} onChange={e => setForm({...form, description: e.target.value})} /></div>
              <div className="form-row">
                <div className="form-group"><label>Proficiency (0-100)</label><input type="range" min="0" max="100" value={form.proficiency} onChange={e => setForm({...form, proficiency: parseInt(e.target.value)})} /> {form.proficiency}%</div>
                <div className="form-group"><label>Display Order</label><input type="number" value={form.displayOrder} onChange={e => setForm({...form, displayOrder: parseInt(e.target.value)})} /></div>
              </div>
              <div className="form-group"><label>Icon Class (devicon)</label><input value={form.iconClass} onChange={e => setForm({...form, iconClass: e.target.value})} placeholder="devicon-dotnetcore-plain" /></div>
              <div className="form-group"><label>Category</label><select value={form.category} onChange={e => setForm({...form, category: parseInt(e.target.value)})}>
                {categories.map((c, i) => <option key={i} value={i}>{c}</option>)}
              </select></div>
              <div className="modal-actions">
                <button type="button" className="btn btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary">{editing ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageSkills;
